import React, { RefObject } from 'react';

import { DragAndDropType } from '../../../types';
import { removeRowBorder, removeRowClass, setRowBorder, setRowClas } from '../projectTreeUtil';

export abstract class DragStrategy {
    static CSS_CLASS = {
        borderTopBottom: 'drag-border-top-bottom',
        borderLeft: 'drag-border-left',
        borderRight: 'drag-border-right',
        dragOverUp: 'drag-over-up',
        dragOverDown: 'drag-over-down',
        itemBeingDragged: 'item-being-dragged',
    };

    static DATA_ITEM = {
        dataItemId: 'data-item-id',
        dataItemProjectId: 'data-item-project-id',
        dataDragType: 'data-drag-type',
    };

    constructor(private readonly rowsMap: Map<string, RefObject<HTMLDivElement>[]>) {}

    protected dragOverItemId = '';
    protected itemBeingDraggedId = '';
    protected itemBeingDraggedProjectId = '';

    onDragOver(e: React.DragEvent<HTMLDivElement>, access?: boolean): boolean {
        if (!access) {
            return false;
        }

        e.dataTransfer.dropEffect = 'move';
        e.preventDefault();

        this.dragOverItemId = DragStrategy.getItemId(e);
        return this.dragOverItemId !== this.itemBeingDraggedId;
    }

    abstract onDragLeave(e: React.DragEvent<HTMLDivElement>): void;
    onDragEnter(e: React.DragEvent<HTMLDivElement>) {
        e.dataTransfer.dropEffect = 'move';
        e.preventDefault();
    }

    onDragStart(e: React.DragEvent<HTMLDivElement>): boolean {
        this.itemBeingDraggedId = DragStrategy.getItemId(e);
        this.itemBeingDraggedProjectId = this.getProjectId(e);
        if (!this.itemBeingDraggedId || !this.itemBeingDraggedProjectId) {
            return false;
        }

        this.setRowClass(this.itemBeingDraggedId, DragStrategy.CSS_CLASS.itemBeingDragged);

        return true;
    }

    onDragEnd(e: React.DragEvent<HTMLDivElement>): string | null {
        this.removeRowClass(DragStrategy.getItemId(e), DragStrategy.CSS_CLASS.itemBeingDragged);
        return this.dragOverItemId ? this.dragOverItemId : null;
    }

    protected getProjectId = (e: React.DragEvent<HTMLDivElement>) => {
        return e.currentTarget.getAttribute(DragStrategy.DATA_ITEM.dataItemProjectId) ?? '';
    };

    static getItemId = (e: React.DragEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
        return e.currentTarget.getAttribute(DragStrategy.DATA_ITEM.dataItemId) ?? '';
    };

    static getDragType = (e: React.DragEvent<HTMLDivElement>): DragAndDropType => {
        return (e.currentTarget.getAttribute(DragStrategy.DATA_ITEM.dataDragType) as DragAndDropType) ?? 'none';
    };

    protected setRowClass = (itemId: string, className: string) => {
        return setRowClas(this.rowsMap, itemId, className);
    };

    protected removeRowClass = (itemId: string, className: string) => {
        return removeRowClass(this.rowsMap, itemId, className);
    };

    protected setRowBorder = (itemId: string) => {
        return setRowBorder(
            itemId,
            this.rowsMap,
            DragStrategy.CSS_CLASS.borderLeft,
            DragStrategy.CSS_CLASS.borderRight,
            DragStrategy.CSS_CLASS.borderTopBottom,
        );
    };

    protected removeRowBorder = (itemId: string) => {
        return removeRowBorder(
            itemId,
            this.rowsMap,
            DragStrategy.CSS_CLASS.borderLeft,
            DragStrategy.CSS_CLASS.borderRight,
            DragStrategy.CSS_CLASS.borderTopBottom,
        );
    };
}
